// Generate a professional A4 PDF (via browser print) for a customer's receivable detail.
// mode: "all" | "piutang" | "pembayaran"

const rp = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");
const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 10);
  return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

// Build item rows for a customer's credit sales (shared by single & bulk PDF)
function salesRowsHtml(sales) {
  let rows = "";
  (sales || []).forEach((s) => {
    const items = s.items || [];
    if (!items.length) {
      rows += `<tr>
        <td>${fmtDate(s.created_at)}</td>
        <td class="mono">${esc(s.number)}</td>
        <td colspan="3" class="muted">Tanpa rincian item</td>
        <td class="right mono">${rp(s.total)}</td></tr>`;
      return;
    }
    items.forEach((it, idx) => {
      rows += `<tr>
        <td>${idx === 0 ? fmtDate(s.created_at) : ""}</td>
        <td class="mono">${idx === 0 ? esc(s.number) : ""}</td>
        <td>${esc(it.name)} <span class="muted">(${esc(it.unit)})</span></td>
        <td class="right mono">${Number(it.qty)}</td>
        <td class="right mono">${rp(it.sell_price || 0)}</td>
        <td class="right mono">${rp(it.subtotal || 0)}</td></tr>`;
    });
  });
  if (!rows) rows = `<tr><td colspan="6" class="muted center">Tidak ada transaksi piutang</td></tr>`;
  return rows;
}

const PDF_CSS = `
    @page { size: A4; margin: 16mm 14mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1e293b; font-size: 12px; margin: 0; }
    .brand { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid #b45309; padding-bottom:10px; margin-bottom:14px; }
    .brand .logo { font-size:22px; font-weight:800; letter-spacing:-0.5px; color:#b45309; }
    .brand .sub { font-size:11px; color:#64748b; }
    .doc-title { text-align:right; }
    .doc-title h1 { font-size:15px; margin:0; text-transform:uppercase; letter-spacing:0.5px; }
    .doc-title .meta { font-size:10px; color:#64748b; margin-top:2px; }
    .profile { display:flex; justify-content:space-between; gap:16px; background:#fff7ed; border:1px solid #fed7aa; border-radius:8px; padding:12px 14px; margin-bottom:16px; }
    .profile .field { margin-bottom:4px; }
    .profile .label { font-size:10px; color:#64748b; text-transform:uppercase; }
    .profile .value { font-weight:600; }
    .outstanding { text-align:right; }
    .outstanding .label { font-size:10px; color:#92400e; text-transform:uppercase; }
    .outstanding .value { font-size:22px; font-weight:800; color:#b45309; font-family:monospace; }
    h3 { font-size:12px; text-transform:uppercase; letter-spacing:0.4px; color:#334155; border-bottom:1px solid #e2e8f0; padding-bottom:4px; margin:16px 0 8px; }
    table { width:100%; border-collapse:collapse; margin-bottom:6px; }
    th, td { padding:6px 8px; border-bottom:1px solid #e2e8f0; text-align:left; vertical-align:top; }
    thead th { background:#f1f5f9; font-size:10px; text-transform:uppercase; color:#475569; border-bottom:1.5px solid #cbd5e1; }
    tfoot td { border-top:1.5px solid #cbd5e1; border-bottom:none; background:#f8fafc; }
    .right { text-align:right; } .center { text-align:center; } .mono { font-family:monospace; }
    .muted { color:#94a3b8; } .bold { font-weight:700; }
    .summary { margin-top:18px; margin-left:auto; width:60%; }
    .srow { display:flex; justify-content:space-between; padding:6px 10px; font-size:12px; }
    .srow.total { border-top:2px solid #b45309; margin-top:4px; font-weight:800; font-size:14px; color:#b45309; }
    .srow .paid { color:#059669; }
    .foot { margin-top:30px; font-size:10px; color:#94a3b8; text-align:center; border-top:1px solid #e2e8f0; padding-top:8px; }
    .cust-block { page-break-inside: avoid; margin-bottom:22px; }
    .cust-head { display:flex; justify-content:space-between; align-items:center; background:#f8fafc; border-left:4px solid #b45309; padding:8px 12px; margin-bottom:6px; }
    .cust-head .nm { font-weight:700; font-size:13px; }
    .cust-head .ph { font-size:10px; color:#64748b; }
    .cust-head .sisa { text-align:right; }
    .cust-head .sisa .l { font-size:9px; color:#92400e; text-transform:uppercase; }
    .cust-head .sisa .v { font-family:monospace; font-weight:800; color:#b45309; font-size:14px; }
    .ov { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:16px; }
    .ov .box { border:1px solid #e2e8f0; border-radius:8px; padding:10px 12px; }
    .ov .box .l { font-size:9px; color:#64748b; text-transform:uppercase; }
    .ov .box .v { font-family:monospace; font-weight:700; font-size:15px; margin-top:2px; }
`;

function openPrint(html) {
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 400);
  return true;
}

export function printPiutangPdf(detail, mode = "all") {
  const c = detail.customer || {};
  const sales = detail.credit_sales || [];
  const payments = detail.payments || [];

  const showPiutang = mode === "all" || mode === "piutang";
  const showPembayaran = mode === "all" || mode === "pembayaran";

  const modeLabel =
    mode === "piutang" ? "Daftar Piutang (Sisa Tagihan)" : mode === "pembayaran" ? "Riwayat Pembayaran" : "Gabungan Piutang & Pembayaran";

  // Section 1 — receivable transactions with items
  const piutangRows = salesRowsHtml(sales);

  const piutangSection = showPiutang
    ? `<h3>Daftar Piutang / Transaksi Kas Bon</h3>
       <table>
         <thead><tr>
           <th>Tanggal</th><th>No. Invoice</th><th>Item / Barang</th>
           <th class="right">Qty</th><th class="right">Harga Satuan</th><th class="right">Subtotal</th>
         </tr></thead>
         <tbody>${piutangRows}</tbody>
         <tfoot><tr><td colspan="5" class="right bold">Total Piutang</td><td class="right mono bold">${rp(detail.total_credit)}</td></tr></tfoot>
       </table>`
    : "";

  // Section 2 — payments
  let payRows = "";
  payments.forEach((p) => {
    payRows += `<tr>
      <td>${fmtDate(p.date || p.created_at)}</td>
      <td>${p.method === "cash" || !p.method ? "Tunai" : esc(p.method)}</td>
      <td class="mono">${esc(p.number)}</td>
      <td>${esc(p.branch_name || "-")}</td>
      <td class="right mono">${rp(p.amount)}</td></tr>`;
  });
  if (!payRows) payRows = `<tr><td colspan="5" class="muted center">Belum ada pembayaran</td></tr>`;

  const paySection = showPembayaran
    ? `<h3>Daftar Pembayaran / Pelunasan</h3>
       <table>
         <thead><tr>
           <th>Tanggal Bayar</th><th>Metode</th><th>No. Bukti Bayar</th><th>Cabang</th><th class="right">Nominal</th>
         </tr></thead>
         <tbody>${payRows}</tbody>
         <tfoot><tr><td colspan="4" class="right bold">Total Dibayar</td><td class="right mono bold">${rp(detail.total_paid)}</td></tr></tfoot>
       </table>`
    : "";

  const summary = `
    <div class="summary">
      <h3>Ringkasan</h3>
      <div class="srow"><span>Total Seluruh Piutang (Awal)</span><span class="mono">${rp(detail.total_credit)}</span></div>
      <div class="srow"><span>Total Sudah Dibayar / Dilunasi</span><span class="mono paid">- ${rp(detail.total_paid)}</span></div>
      <div class="srow total"><span>Sisa Piutang Belum Dibayar</span><span class="mono">${rp(detail.outstanding)}</span></div>
    </div>`;

  const html = `<!DOCTYPE html><html lang="id"><head><meta charset="utf-8" />
  <title>Detail Piutang - ${esc(c.name)}</title>
  <style>${PDF_CSS}</style></head><body>
    <div class="brand">
      <div><div class="logo">TOKO KU</div><div class="sub">Manajemen Multi-Cabang</div></div>
      <div class="doc-title"><h1>Detail Piutang Pelanggan</h1><div class="meta">${esc(modeLabel)}</div><div class="meta">Dicetak: ${fmtDate(new Date().toISOString())}</div></div>
    </div>
    <div class="profile">
      <div>
        <div class="field"><div class="label">Nama Pelanggan</div><div class="value">${esc(c.name)}</div></div>
        <div class="field"><div class="label">Telepon</div><div class="value">${esc(c.phone || "-")}</div></div>
        <div class="field"><div class="label">Alamat</div><div class="value">${esc(c.address || "-")}</div></div>
      </div>
      <div class="outstanding"><div class="label">Total Sisa Piutang</div><div class="value">${rp(detail.outstanding)}</div></div>
    </div>
    ${piutangSection}
    ${paySection}
    ${summary}
    <div class="foot">Dokumen ini dihasilkan otomatis oleh sistem TOKO KU.</div>
  </body></html>`;

  return openPrint(html);
}


// Bulk: one combined A4 document for all customers with outstanding receivables.
export function printPiutangBulkPdf(details) {
  const list = details || [];
  const totCredit = list.reduce((s, d) => s + (d.total_credit || 0), 0);
  const totPaid = list.reduce((s, d) => s + (d.total_paid || 0), 0);
  const totOut = list.reduce((s, d) => s + (d.outstanding || 0), 0);

  let blocks = "";
  list.forEach((d, i) => {
    const c = d.customer || {};
    blocks += `<div class="cust-block">
      <div class="cust-head">
        <div><div class="nm">${i + 1}. ${esc(c.name)}</div><div class="ph">${esc(c.phone || "-")}${c.address ? " · " + esc(c.address) : ""}</div></div>
        <div class="sisa"><div class="l">Sisa Piutang</div><div class="v">${rp(d.outstanding)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Tanggal</th><th>No. Invoice</th><th>Item / Barang</th>
          <th class="right">Qty</th><th class="right">Harga Satuan</th><th class="right">Subtotal</th>
        </tr></thead>
        <tbody>${salesRowsHtml(d.credit_sales)}</tbody>
        <tfoot>
          <tr><td colspan="5" class="right">Total Kas Bon</td><td class="right mono">${rp(d.total_credit)}</td></tr>
          <tr><td colspan="5" class="right">Total Dibayar</td><td class="right mono paid">- ${rp(d.total_paid)}</td></tr>
          <tr><td colspan="5" class="right bold">Sisa Piutang</td><td class="right mono bold">${rp(d.outstanding)}</td></tr>
        </tfoot>
      </table>
    </div>`;
  });
  if (!blocks) blocks = `<div class="muted center" style="padding:40px">Tidak ada pelanggan berpiutang</div>`;

  const html = `<!DOCTYPE html><html lang="id"><head><meta charset="utf-8" />
  <title>Rekap Piutang Seluruh Pelanggan</title>
  <style>${PDF_CSS}</style></head><body>
    <div class="brand">
      <div><div class="logo">TOKO KU</div><div class="sub">Manajemen Multi-Cabang</div></div>
      <div class="doc-title"><h1>Rekap Piutang Seluruh Pelanggan</h1><div class="meta">${list.length} pelanggan berpiutang</div><div class="meta">Dicetak: ${fmtDate(new Date().toISOString())}</div></div>
    </div>
    <div class="ov">
      <div class="box"><div class="l">Jumlah Pelanggan</div><div class="v">${list.length}</div></div>
      <div class="box"><div class="l">Total Piutang (Awal)</div><div class="v">${rp(totCredit)}</div></div>
      <div class="box"><div class="l">Total Dibayar</div><div class="v" style="color:#059669">${rp(totPaid)}</div></div>
      <div class="box"><div class="l">Total Sisa Piutang</div><div class="v" style="color:#b45309">${rp(totOut)}</div></div>
    </div>
    ${blocks}
    <div class="foot">Dokumen ini dihasilkan otomatis oleh sistem TOKO KU.</div>
  </body></html>`;

  return openPrint(html);
}
